import _ from 'lodash';

/**
 * 
 * Cart Item
 * [{ 
 *      ...product,
 *      quantity: 2,
 * }]
 */

// Effective unit price: prefer the discount/sale price when present, else the regular price
export const getEffectiveUnitPrice = (item) => {
    const price =
        item?.discount_price && Number(item.discount_price) > 0
            ? item.discount_price
            : item?.product_price || item?.price;
    return parseFloat(price) || 0;
}

// Sum of all selected addon option prices for one cart item.
// Guest items carry `selected_addons` (group_id/name/selected_options); the logged-in
// cart API returns a flat `addons` array (addon_option_id/addon_name/quantity/price/group_name)
export const getAddonTotal = (item) => {
    if (Array.isArray(item?.selected_addons)) {
        return item.selected_addons.reduce((sum, group) => {
            return sum + (group.selected_options || []).reduce((s, opt) => {
                return s + (parseFloat(opt?.price) || 0);
            }, 0);
        }, 0);
    }
    if (Array.isArray(item?.addons)) {
        return item.addons.reduce((sum, addon) => {
            return sum + (parseFloat(addon?.price) || 0) * (Number(addon?.quantity) || 1);
        }, 0);
    }
    return 0;
}

// Sum of VAT across an item's addons. The product API now includes a per-unit
// `vat_amount` on each addon option, carried into `selected_options` at
// add-to-cart time (one entry per unit selected, so summing directly across
// entries already accounts for quantity — same pattern as getAddonTotal above).
export const getAddonVatTotal = (item) => {
    if (Array.isArray(item?.selected_addons)) {
        return item.selected_addons.reduce((sum, group) => {
            return sum + (group.selected_options || []).reduce((s, opt) => {
                return s + (parseFloat(opt?.vat_amount) || 0);
            }, 0);
        }, 0);
    }
    if (Array.isArray(item?.addons)) {
        return item.addons.reduce((sum, addon) => sum + (parseFloat(addon?.vat_amount) || 0), 0);
    }
    return 0;
}

// One guest cart line per product, regardless of addon choice — picking
// different addons for a product that's already in the cart updates that
// line's addons rather than creating a second line for the same product.
const findCartLineIndex = (cartItems, product) =>
    _.findIndex(cartItems, (item) => item.id === product.id);

// Capitalize just the first letter, leaving the rest of the string as-is
// (unlike lodash's capitalize, which lowercases the remainder)
const capitalizeFirst = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

// Flatten a cart/order item's addons into one badge per option: { key, name, quantity, price },
// regardless of whether the item came from the guest localStorage cart (`selected_addons`,
// one entry per unit selected), the logged-in cart API (`addons`, already quantity-aggregated,
// price on `price`), or an order-detail API response (`addons`, quantity-aggregated, price on `unit_price`)
export const getCartItemAddonBadges = (item) => {
    if (Array.isArray(item?.selected_addons) && item.selected_addons.length > 0) {
        const counts = new Map();
        item.selected_addons.forEach((group) => {
            (group.selected_options || []).forEach((opt) => {
                const key = `${group.group_id}-${opt?.option_id}`;
                const existing = counts.get(key);
                if (existing) {
                    existing.quantity += 1;
                } else {
                    counts.set(key, {
                        key,
                        groupId: group.group_id,
                        optionId: opt?.option_id,
                        name: capitalizeFirst(opt?.name || ""),
                        price: parseFloat(opt?.price) || 0,
                        quantity: 1,
                        // Per-unit VAT from the product API, same as `price` above —
                        // the fallback lineTotal formula multiplies both by quantity.
                        vatAmount: parseFloat(opt?.vat_amount) || 0,
                    });
                }
            });
        });
        return Array.from(counts.values());
    }
    if (Array.isArray(item?.addons) && item.addons.length > 0) {
        return item.addons.map((a) => ({
            key: a.id ?? a.addon_option_id,
            name: capitalizeFirst(a.addon_name || ""),
            price: parseFloat(a.price ?? a.unit_price) || 0,
            quantity: Number(a.quantity) || 1,
            vatAmount: parseFloat(a.vat_amount) || 0,
            // Backend-computed line total (price * quantity + vat * quantity).
            // Preferred over a client-side recompute so it stays correct as
            // quantity changes, matching product_subtotal_price on the item.
            subtotalPrice: a.subtotal_price != null ? parseFloat(a.subtotal_price) : undefined,
        }));
    }
    return [];
}

// Convert the display-shape `selected_addons` (group_id/name/selected_options[{option_id,name,price}],
// one entry per unit selected) into the addons payload /customer/cart-without-login/ expects:
// [{addon_option_id, quantity}], aggregating repeated option entries into a quantity.
export const toAddonsApiPayload = (selectedAddons) => {
    if (!Array.isArray(selectedAddons)) return [];
    const counts = new Map();
    selectedAddons.forEach((group) => {
        (Array.isArray(group?.selected_options) ? group.selected_options : []).forEach((opt) => {
            if (opt?.option_id == null) return;
            counts.set(opt.option_id, (counts.get(opt.option_id) || 0) + 1);
        });
    });
    return Array.from(counts.entries()).map(([addon_option_id, quantity]) => ({
        addon_option_id,
        quantity,
    }));
}

// Add or remove one unit of a specific addon option on a guest cart item, mirroring how
// updateCartItemsToLocalStorage adjusts whole-product quantity. Quantity is represented by
// duplicating/removing entries within the group's selected_options (one entry per unit).
export const updateCartItemAddonInLocalStorage = (product, groupId, optionId, action) => {
    let cartItems = getCartItemsFromLocalStorage();
    let itemIndex = findCartLineIndex(cartItems, product);
    if (itemIndex < 0) return;

    let groups = Array.isArray(cartItems[itemIndex].selected_addons)
        ? [...cartItems[itemIndex].selected_addons]
        : [];
    let groupIndex = _.findIndex(groups, (g) => g.group_id === groupId);
    if (groupIndex < 0) return;

    let options = Array.isArray(groups[groupIndex].selected_options)
        ? [...groups[groupIndex].selected_options]
        : [];

    if (action == 1) {
        // Duplicate an existing instance of this option to bump its quantity by one unit
        let existing = options.find((opt) => opt.option_id === optionId);
        if (existing) options.push({ ...existing });
    } else if (action == 0) {
        let lastIndex = _.findLastIndex(options, (opt) => opt.option_id === optionId);
        if (lastIndex >= 0) options.splice(lastIndex, 1);
    }

    groups[groupIndex] = { ...groups[groupIndex], selected_options: options };
    cartItems[itemIndex] = { ...cartItems[itemIndex], selected_addons: groups };
    localStorage.setItem('cartItems', JSON.stringify(cartItems));
}

// Add an addon option to a guest cart item that doesn't have it selected yet
// (as opposed to updateCartItemAddonInLocalStorage above, which only bumps
// the quantity of an option already present). Single-choice groups
// (max_select === 1) replace whatever was picked instead of stacking, same
// as the product-page AddonSelector.
export const addCartItemAddonOptionToLocalStorage = (product, group, option) => {
    let cartItems = getCartItemsFromLocalStorage();
    let itemIndex = findCartLineIndex(cartItems, product);
    if (itemIndex < 0) return;

    let groups = Array.isArray(cartItems[itemIndex].selected_addons)
        ? [...cartItems[itemIndex].selected_addons]
        : [];
    let groupIndex = _.findIndex(groups, (g) => g.group_id === group.group_id);

    const newOption = {
        option_id: option.option_id,
        name: option.name,
        price: option.price,
        vat_amount: option.vat_amount,
    };

    if (groupIndex >= 0) {
        const options =
            group.max_select === 1
                ? [newOption]
                : [...(groups[groupIndex].selected_options || []), newOption];
        groups[groupIndex] = { ...groups[groupIndex], selected_options: options };
    } else {
        groups = [
            ...groups,
            { group_id: group.group_id, name: group.name, selected_options: [newOption] },
        ];
    }

    cartItems[itemIndex] = { ...cartItems[itemIndex], selected_addons: groups };
    localStorage.setItem('cartItems', JSON.stringify(cartItems));
}

// Remove every instance of a specific addon option from a guest cart item
export const removeCartItemAddonFromLocalStorage = (product, groupId, optionId) => {
    let cartItems = getCartItemsFromLocalStorage();
    let itemIndex = findCartLineIndex(cartItems, product);
    if (itemIndex < 0) return;

    let groups = Array.isArray(cartItems[itemIndex].selected_addons)
        ? [...cartItems[itemIndex].selected_addons]
        : [];
    let groupIndex = _.findIndex(groups, (g) => g.group_id === groupId);
    if (groupIndex < 0) return;

    let options = (groups[groupIndex].selected_options || []).filter(
        (opt) => opt.option_id !== optionId,
    );

    groups[groupIndex] = { ...groups[groupIndex], selected_options: options };
    cartItems[itemIndex] = { ...cartItems[itemIndex], selected_addons: groups };
    localStorage.setItem('cartItems', JSON.stringify(cartItems));
}

// Function to retrieve cart items from local storage
export const getCartItemsFromLocalStorage = () => {
    const cartItemsJson = localStorage.getItem('cartItems');
    return cartItemsJson ? JSON.parse(cartItemsJson) : [];
}

// Function to add cart items to local storage
export const addCartItemsToLocalStorage = (product) => {
    let cartItems = getCartItemsFromLocalStorage();
    // Re-adding a product that's already in the cart with a new addon
    // selection updates that single line's addons (and bumps its quantity)
    // rather than creating a second line for the same product.
    let itemIndex = findCartLineIndex(cartItems, product)
    if (itemIndex >= 0) {
        const quantity = cartItems[itemIndex].quantity
        cartItems[itemIndex] = {
            ...product,
            quantity: quantity + 1
        }
    } else {
        cartItems.push({
            ...product,
            quantity: 1
        })
    }
    localStorage.setItem('cartItems', JSON.stringify(cartItems));
}

// Replace the addon selection on a guest cart line the product already has,
// leaving quantity untouched — used when the product page is editing an
// item already in the cart (addon change) rather than adding another unit.
// Falls back to creating the line at quantity 1 if it's not actually there.
export const updateCartItemAddonsOnlyInLocalStorage = (product) => {
    let cartItems = getCartItemsFromLocalStorage();
    let itemIndex = findCartLineIndex(cartItems, product)
    if (itemIndex >= 0) {
        cartItems[itemIndex] = {
            ...product,
            quantity: cartItems[itemIndex].quantity
        }
    } else {
        cartItems.push({
            ...product,
            quantity: 1
        })
    }
    localStorage.setItem('cartItems', JSON.stringify(cartItems));
}

// Function to add cart items to local storage
export const updateCartItemsToLocalStorage = (product, action) => {
    let cartItems = getCartItemsFromLocalStorage();
    let itemIndex = findCartLineIndex(cartItems, product)
    if (itemIndex >= 0) {
        const quantity = cartItems[itemIndex].quantity
        if (action == 1) 
            cartItems[itemIndex] = {
                ...product,
                quantity: quantity + 1
            }
        else if (action == 0 && quantity == 1) {
            removeCartItemsFromLocalStorage(product)
            return;
        } else if (action == 0) {
            cartItems[itemIndex] = {
                ...product,
                quantity: quantity - 1
            }
        }
    } else if (action == 1) {
        cartItems.push({
            ...product,
            quantity: 1
        })
    }
    localStorage.setItem('cartItems', JSON.stringify(cartItems));
}

// Function to remove cart items to local storage
export const removeCartItemsFromLocalStorage = (product) => {
    let cartItems = getCartItemsFromLocalStorage();
    let itemIndex = findCartLineIndex(cartItems, product)
    if (itemIndex >= 0) {
        cartItems.splice(itemIndex, 1)
    }
    localStorage.setItem('cartItems', JSON.stringify(cartItems));
}

// Example usage: adding an item to the cart
export const getCartCountFromLocalStorage = (item) => {
    let cartItems = getCartItemsFromLocalStorage();
    let count = 0;
    for (let item of cartItems) {
        count = count + item.quantity
    }
    return count;
}
export const getCartFromLocalStorage = (item) => {
    let cartItems = getCartItemsFromLocalStorage();
    let subTotal = 0;
    cartItems.map((item) =>
        // Include VAT and addon prices (+ addon VAT) so the guest subtotal is accurate.
        // Addon cost applies once per cart line (its own quantity stepper is
        // independent of the product's), so it isn't multiplied by item.quantity.
        subTotal += item?.quantity * (getEffectiveUnitPrice(item) + (Number(item?.vat_amount) || 0)) + getAddonTotal(item) + getAddonVatTotal(item)
    )

    return subTotal;
}

// Example usage: clear cart
export const clearCart = (item) => {
    localStorage.setItem('cartItems', '');
}

// Example usage: calculate cart summary
export const getCartSummaryFromLocalStorage = (item) => {
    let cartItems = getCartItemsFromLocalStorage();
    let subTotal = 0;
    for (let item of cartItems) {
        if (item.count != 0 && item.count >= item.quantity)
            subTotal = subTotal + getEffectiveUnitPrice(item) * item.quantity + getAddonTotal(item);
    }
    return {
        cart_total: subTotal
    };
}

// Example usage: adding an item to the cart
export const addToCart = (item) => {
    let cartItems = getCartItemsFromLocalStorage();
    cartItems.push(item);
    addCartItemsToLocalStorage(cartItems);
}

// Example usage: retrieving cart items
// const cartItems = getCartItemsFromLocalStorage();